import { REACT_APP_IFRAMELY_API_KEY } from 'env'
import { useEffect, useState } from 'react'
import { get } from 'request-dot-js'
import { truncate } from 'utils'
import { reportError } from 'utils/error'
import { useBrand } from 'hooks/useBrand'
import Styles from './LinkMetaData.module.scss'
import { Button } from 'blaze-ui/Button'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Trash2 } from 'blaze-ui/icons'

interface Props {
  url: string
  onEdit: () => void
  onDelete: () => void
  isEditEnabled: boolean
}

export const EXTERNAL_LINK_REL = 'noopener noreferrer nofollow ugc'

async function fetchFaviconAndTitle(url: string): Promise<{ title?: string; faviconUrl?: string }> {
  try {
    const res = await get(`https://iframe.ly/api/iframely?key=${REACT_APP_IFRAMELY_API_KEY}&url=${url}`)
    if (res.type !== 'success' || res.data?.error) return {}

    return { title: res.data.meta.title, faviconUrl: res.data.links.icon?.[0]?.href }
  } catch (error) {
    reportError(error, {
      severity: 'error',
      metaData: { url },
    })
    return {}
  }
}

export const LinkMetaData = ({ url, onEdit, onDelete, isEditEnabled }: Props) => {
  const [domainName] = useState(new URL(url).hostname)
  const [urlTitle, setTitle] = useState('')
  const { favicon: defaultFavicon } = useBrand()
  const [faviconImgSrc, setFaviconImgSrc] = useState(
    `https://s2.googleusercontent.com/s2/favicons?domain_url=${domainName}?sz=64`,
  )

  useEffect(() => {
    fetchFaviconAndTitle(url).then(({ title, faviconUrl }) => {
      title && setTitle(title)
      faviconUrl && setFaviconImgSrc(faviconUrl)
    })
  }, [url])

  const handleFaviconError = () => faviconImgSrc !== defaultFavicon && setFaviconImgSrc(defaultFavicon)

  return (
    <div className={Styles.LinkMetadata}>
      <div className={Styles.successFetchingContainer}>
        {/* eslint-disable-next-line react/jsx-no-target-blank */}
        <a className={Styles.title} target="_blank" rel={EXTERNAL_LINK_REL} href={url}>
          <div className={Styles.linkPreviewContainer}>
            <div className={Styles.faviconContainer}>
              <img
                alt={`The favicon for ${domainName}`}
                className={Styles.favicon}
                src={faviconImgSrc}
                onError={handleFaviconError}
              />
            </div>
            <div className={Styles.urlDataContainer}>
              <div>{truncate(urlTitle || domainName, 35)}</div>
              <p className={Styles.url}>{truncate(url, 37)}</p>
            </div>
          </div>
        </a>
        {isEditEnabled && (
          <div className={Styles.buttons}>
            <Button variant="tertiary" size="sm" onPress={onEdit} preventFocusOnPress>
              Edit Link
            </Button>
            <IconButton
              variant="tertiary"
              size="sm"
              icon={Trash2}
              preventFocusOnPress
              onPress={onDelete}
              aria-label="Delete Link"
            />
          </div>
        )}
      </div>
    </div>
  )
}
